import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  AppDialogService,
  ConfirmationDialogComponent,
  DateTimePipe,
  ErrorComponent,
  HeaderBarComponent,
  LoadingIndicatorComponent,
  Organization,
  OrganizationService,
  OrganizationStatus,
  AppModelModalComponent,
} from '@nimmsta/ng-commons-lib';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OrganizationUserComponent } from './organization-user.component';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationTypePipe } from './organization-type.pipe';
import { OrganizationFormComponent } from './organization-form.component';
import { OrganizationLicenseUsageReportComponent } from './organization-license-usage-report.component';
import { OrganizationLicenseEntitlementsFormComponent } from './organization-license-entitlements-form.component';
import { take } from 'rxjs';

@Component({
  selector: 'app-organization-details',
  templateUrl: 'organization-details.component.html',
  imports: [
    OrganizationUserComponent,
    HeaderBarComponent,
    LoadingIndicatorComponent,
    ErrorComponent,
    TranslatePipe,
    DateTimePipe,
    OrganizationTypePipe,
    AppModelModalComponent,
    OrganizationFormComponent,
    OrganizationLicenseUsageReportComponent,
    OrganizationLicenseEntitlementsFormComponent,
  ],
})
export class OrganizationDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private orgService = inject(OrganizationService);
  private modalService = inject(AppDialogService);
  private translate = inject(TranslateService);

  organization?: Organization;

  error?: HttpErrorResponse;

  isLoading = false;

  actionLoading = false;

  editing = false;

  readonly usersExpanded = signal(true);

  private organizationId!: string;

  ngOnInit(): void {
    const orgId = this.route.snapshot.paramMap.get('id');
    if (!orgId) {
      throw new Error('Organization id is required');
    }
    this.organizationId = orgId;
    this.loadOrganization();
  }

  private loadOrganization(): void {
    this.isLoading = true;
    this.orgService.get(this.organizationId).subscribe({
      next: (organization) => {
        this.organization = organization;
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
        this.isLoading = false;
      },
    });
  }

  get ownerName(): string {
    const owner = this.organization?.owner;
    if (!owner) {
      return '-';
    }

    const fullName = [owner.given_name, owner.family_name]
      .filter(Boolean)
      .join(' ');
    return fullName || owner.email || '-';
  }

  get statusActionLabel(): string {
    return this.organization?.status === OrganizationStatus.ACTIVE
      ? 'organizations.decline'
      : 'organizations.approve';
  }

  get organizationActive(): boolean {
    return this.organization?.status === OrganizationStatus.ACTIVE;
  }

  toggleOrganizationStatus(): void {
    if (!this.organization || this.actionLoading || this.editing) {
      return;
    }

    const status =
      this.organization.status === OrganizationStatus.ACTIVE
        ? OrganizationStatus.INACTIVE
        : OrganizationStatus.ACTIVE;
    const updatedOrganization = {
      ...this.organization,
      status,
    } as Organization;

    this.actionLoading = true;
    this.error = undefined;
    this.orgService.createOrUpdate(updatedOrganization).subscribe({
      next: (organization) => {
        this.organization = organization;
        this.actionLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
        this.actionLoading = false;
      },
    });
  }

  editOrganization(organizationForm: OrganizationFormComponent): void {
    if (!this.organization || this.actionLoading) {
      return;
    }

    organizationForm.editable = true;
    organizationForm.setValue({ ...this.organization } as Organization);
    organizationForm.loadOrganizations();
    this.editing = true;
  }

  editLicenseEntitlements(
    licenseModal: AppModelModalComponent<Organization>,
    licenseEntitlementsForm: OrganizationLicenseEntitlementsFormComponent,
  ): void {
    if (!this.organization || this.actionLoading) {
      return;
    }

    licenseModal.title = this.translate.instant(
      'organizations.licenseEntitlements.title',
      { organization: this.organization.name },
    );
    licenseEntitlementsForm.load(this.organization);
    licenseModal.show();
  }

  deleteOrganization(): void {
    if (!this.organization || this.actionLoading) {
      return;
    }

    const organization = this.organization;
    const modalRef = this.modalService.open<
      ConfirmationDialogComponent,
      unknown,
      boolean
    >(ConfirmationDialogComponent, {
      data: {
        title: 'delete?',
        message: 'delete_question',
        itemLabel: 'organizations.name',
        itemLabelTranslate: true,
        itemValue: organization.name,
        cancelLabel: 'cancel',
        confirmLabel: 'delete',
        confirmButtonClass: 'btn-danger',
      },
    });

    modalRef.closed.pipe(take(1)).subscribe((confirmed) => {
      if (confirmed !== true) {
        return;
      }

      this.actionLoading = true;
      this.error = undefined;
      this.orgService.delete(organization).subscribe({
        next: () => {
          this.actionLoading = false;
          void this.router.navigate(['/organizations']);
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
          this.actionLoading = false;
        },
      });
    });
  }

  onSave(organizationForm: OrganizationFormComponent): void {
    organizationForm.onSave();
  }

  cancelEdit(organizationForm: OrganizationFormComponent): void {
    if (!this.organization || this.actionLoading) {
      return;
    }

    organizationForm.setValue({ ...this.organization } as Organization);
    this.editing = false;
  }

  onFormClose(organizationForm: OrganizationFormComponent): void {
    this.organization = organizationForm.value;
    this.editing = false;
  }

  onLoading(loading: boolean): void {
    this.actionLoading = loading;
  }

  toggleUsers(): void {
    this.usersExpanded.update((expanded) => !expanded);
  }
}
